/**
 * WebSocket client for real-time trading events.
 *
 * Usage:
 *   const ws = new TradingWebSocket(onMessage);
 *   ws.connect();
 *   // later:
 *   ws.disconnect();
 */

import { WsEvent } from '../types/trading';
import { getToken } from './authService';

const WS_BASE = (process.env.REACT_APP_WS_URL || 'ws://localhost:8000') + '/ws';

type MessageHandler = (event: WsEvent) => void;
type StatusHandler = (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void;

export class TradingWebSocket {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private shouldReconnect = true;
  private reconnectDelay = 2000;

  constructor(
    private onMessage: MessageHandler,
    private onStatus?: StatusHandler,
  ) {}

  connect(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;

    const token = getToken();
    const wsUrl = token ? `${WS_BASE}?token=${encodeURIComponent(token)}` : WS_BASE;

    this.onStatus?.('connecting');
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      this.onStatus?.('connected');
      this.reconnectDelay = 2000;   // reset backoff on successful connect
      // Send a ping every 20s to keep the connection alive
      this.pingTimer = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send('ping');
        }
      }, 20000);
    };

    this.ws.onmessage = (ev: MessageEvent) => {
      try {
        const parsed: WsEvent = JSON.parse(ev.data);
        this.onMessage(parsed);
      } catch {
        // Ignore non-JSON frames (e.g., pong)
      }
    };

    this.ws.onerror = () => {
      this.onStatus?.('error');
    };

    this.ws.onclose = () => {
      this.onStatus?.('disconnected');
      this._clearTimers();
      if (this.shouldReconnect) {
        this.reconnectTimer = setTimeout(() => {
          this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000);
          this.connect();
        }, this.reconnectDelay);
      }
    };
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this._clearTimers();
    this.ws?.close();
    this.ws = null;
  }

  private _clearTimers(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }
}
